import { useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import { ChevronDownIcon } from "@heroicons/react/24/outline";
import { tagPropType } from "../utilities/recordPropTypes";

/**
 * Reusable multi-select dropdown component for tags.
 * Supports both string arrays (legacy) and object arrays ({ _id, name }) for new Tag model.
 *
 * @param {Object} props
 * @param {Array} props.availableTags - Array of all selectable tags (strings or {_id, name} objects)
 * @param {Array} props.selectedTags - Array of currently selected tags (IDs as strings, or tag objects)
 * @param {Function} props.onSelectionChange - Callback when selection changes (receives new array of IDs)
 * @param {string} props.labelPrefix - Prefix for the button label (e.g., "Tags")
 * @param {string} props.bgColor - Background color class (e.g., "bg-gray-200")
 * @param {boolean} props.readOnly - If true, disables editing and shows only selected tags
 */
export default function TagSelectDropdown({
  availableTags,
  selectedTags,
  onSelectionChange,
  labelPrefix = "Tags",
  bgColor = "bg-gray-200",
  readOnly = false,
  addedTagIds = [],
  removedTagIds = [],
}) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Helper to get tag ID (handles both string and object formats)
  const getTagId = (tag) => {
    if (typeof tag === "string") return tag;
    return tag?._id || tag?.id || tag;
  };

  // Helper to get tag name (handles both string and object formats)
  const getTagName = (tag) => {
    if (typeof tag === "string") return tag;
    return tag?.name || tag?._id || String(tag);
  };

  // Normalize selectedTags to array of IDs for comparison
  const selectedIds = selectedTags.map(getTagId);

  // Click-away listener
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  const handleTagToggle = (tag) => {
    const tagId = getTagId(tag);
    const newSelection = selectedIds.includes(tagId)
      ? selectedIds.filter((id) => id !== tagId)
      : [...selectedIds, tagId];
    onSelectionChange(newSelection);
  };

  const handleClearAll = () => {
    onSelectionChange([]);
  };

  const hasSelection = selectedIds.length > 0;

  // In readOnly mode, only show selectedTags; otherwise show all availableTags
  // When removedTagIds are provided, ensure those tags appear in the list even if unchecked
  const tagsToDisplay = readOnly ? selectedTags : availableTags;
  const removedButMissing = removedTagIds.filter(
    (id) => !tagsToDisplay.some((tag) => getTagId(tag) === id),
  );
  const allTagsToDisplay =
    removedButMissing.length > 0
      ? [
          ...tagsToDisplay,
          ...removedButMissing.map((id) => ({ _id: id, name: id })),
        ]
      : tagsToDisplay;

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center min-h-12 px-4 py-3 text-base border border-gray-700 rounded transition-all duration-200 ${bgColor} text-gray-700 w-full justify-between`}
      >
        <span>
          {labelPrefix}
          {hasSelection && ` (${selectedIds.length})`}
        </span>
        <ChevronDownIcon
          className={`w-4 h-4 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}
        />
      </button>

      {isOpen && (
        <div className="absolute left-0 mt-2 w-64 bg-white border border-gray-700 rounded shadow-lg z-50">
          {/* Clear All button - only show when not readOnly */}
          {!readOnly && hasSelection && (
            <div className="flex gap-2 px-4 py-2 border-b border-gray-200">
              <button
                type="button"
                onClick={handleClearAll}
                className="text-xs text-gray-500 hover:text-red-600 transition-colors"
              >
                Clear All
              </button>
            </div>
          )}
          <div className="py-2 max-h-60 overflow-y-auto">
            {allTagsToDisplay.map((tag) => {
              const tagId = getTagId(tag);
              const tagName = getTagName(tag);
              const isSelected = selectedIds.includes(tagId);
              const isAdded = addedTagIds.includes(tagId.toString());
              const isRemoved = removedTagIds.includes(tagId.toString());

              let nameClass = "text-sm ";
              if (isAdded) {
                nameClass += "text-red-600 font-semibold";
              } else if (isRemoved) {
                nameClass += "text-red-600 line-through";
              } else if (readOnly) {
                nameClass += "text-gray-500";
              } else {
                nameClass += "text-gray-700";
              }

              return (
                <label
                  key={tagId}
                  className={`flex items-center gap-3 px-4 py-2 ${
                    readOnly
                      ? "cursor-default"
                      : "cursor-pointer hover:bg-gray-50"
                  } transition-colors`}
                >
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => !readOnly && handleTagToggle(tag)}
                    disabled={readOnly}
                    className={`w-4 h-4 border-gray-300 rounded ${
                      readOnly
                        ? "text-gray-400 accent-gray-400"
                        : "text-cyan-700 focus:ring-cyan-500 focus:ring-2 accent-cyan-700"
                    }`}
                  />
                  <span className={nameClass}>
                    {tagName}
                  </span>
                </label>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

TagSelectDropdown.propTypes = {
  availableTags: PropTypes.arrayOf(tagPropType).isRequired,
  selectedTags: PropTypes.arrayOf(tagPropType).isRequired,
  onSelectionChange: PropTypes.func.isRequired,
  labelPrefix: PropTypes.string,
  bgColor: PropTypes.string,
  readOnly: PropTypes.bool,
  addedTagIds: PropTypes.arrayOf(PropTypes.string),
  removedTagIds: PropTypes.arrayOf(PropTypes.string),
};
